/**
 * useAgentProgress — React hook that polls /api/agent-progress for a target
 * the user is watching (a universe upload submission, a POM-routed document,
 * etc.).
 *
 * Returns the ordered list of step events. Polls every 2 seconds while the
 * latest event's status is 'started'. Stops polling once the latest event
 * is 'completed' or 'failed'. Pauses while the tab is hidden.
 *
 * Mirrors Core's hook contract exactly — same return shape, same polling
 * cadence — adapted to use Scrubber's SCRUBBER_API_BASE instead of
 * REACT_APP_API_URL.
 *
 * Usage:
 *   const { events, status } = useAgentProgress('scrubber_submission', submissionId);
 *   <AgentThinkingPanel events={events} status={status} />
 */
import { useEffect, useRef, useState } from 'react';
import { SCRUBBER_API_BASE } from '../api/config';

const POLL_MS = 2000;

function latestStatus(events) {
  if (!events || events.length === 0) return 'idle';
  const last = events[events.length - 1];
  return last.step_status || 'idle';
}

export default function useAgentProgress(targetType, targetId) {
  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState('idle');
  const stoppedRef = useRef(false);

  useEffect(() => {
    if (!targetType || !targetId) return undefined;

    let cancelled = false;
    stoppedRef.current = false;

    async function tick() {
      if (cancelled || stoppedRef.current) return;
      if (typeof document !== 'undefined' && document.hidden) {
        // Tab not visible — defer polling.
        setTimeout(tick, POLL_MS);
        return;
      }
      try {
        const url = `${SCRUBBER_API_BASE}/api/agent-progress/${encodeURIComponent(targetType)}/${encodeURIComponent(targetId)}`;
        const r = await fetch(url, { credentials: 'include' });
        if (!cancelled && r.ok) {
          const data = await r.json();
          if (data && data.events) {
            setEvents(data.events);
            const s = latestStatus(data.events);
            setStatus(s);
            if (s === 'completed' || s === 'failed') {
              stoppedRef.current = true;
              return; // Stop polling.
            }
          }
        }
      } catch (_) {
        // Network blip — keep trying.
      }
      if (!cancelled && !stoppedRef.current) {
        setTimeout(tick, POLL_MS);
      }
    }

    tick();

    return () => {
      cancelled = true;
      stoppedRef.current = true;
    };
  }, [targetType, targetId]);

  return { events, status };
}
